/**
 * The words of Content's feed banner and its "Bring all in" confirm (Feed
 * Articles design D–F), from the server's feedBringIn read. Kept apart from
 * FeedBringIn.tsx so they’re tested without rendering.
 */

export interface BringInFeed {
  id: string;
  name: string;
  type?: string | null;
  /** From it, not in TinyCMS yet. */
  waiting: number;
  /** Of those, live on the site now. */
  live: number;
  bringInNew: boolean;
}

export interface BringInJob {
  id: string;
  status: string; // running | done
  dataSourceId?: string | null;
  bringInNew: boolean;
  total: number;
  done: number;
  brought: number;
  live: number;
  drafts: number;
  flagged: number;
  failed: number;
  failedLive: number;
  failedReasons: { gone: number; noTitle: number; error: number };
  /** What flagged is made of, by check, most first (empty from an older server or job). */
  checks?: Array<{ code: string; label: string; count: number }>;
}

/** A check the banner offers as its own filter: "No author · 2,540". */
export interface BannerCheck {
  code: string;
  label: string;
  count: string;
}

/** Checks named on the banner; the rest are under "Show all". */
const CHECKS_SHOWN = 4;

export interface BringInStatus {
  available: boolean;
  liveSource: string;
  feeds: BringInFeed[];
  job: BringInJob | null;
}

const num = (n: number) => n.toLocaleString('en-US');
const articles = (n: number) => `${num(n)} ${n === 1 ? 'article' : 'articles'}`;

/** Seconds per article, for the confirm's estimate (production, a far database). */
const SECONDS_EACH = 0.25;

/** "Takes about 10 minutes." */
export function estimate(count: number): string {
  const minutes = Math.round((count * SECONDS_EACH) / 60);
  if (minutes < 1) return 'Takes under a minute.';
  if (minutes === 1) return 'Takes about a minute.';
  return `Takes about ${minutes} minutes.`;
}

/** The feeds a banner or confirm is about: those with articles still waiting. */
export function waitingFeeds(status: BringInStatus | null | undefined): BringInFeed[] {
  return (status?.feeds || []).filter((f) => f.waiting > 0);
}

/** "ktnewsroom.com/feed", or "your feeds" when there are several. */
export function feedLabel(feeds: Array<Pick<BringInFeed, 'name'>>): string {
  if (feeds.length === 1) return feeds[0].name;
  return 'your feeds';
}

/**
 * A feed by its address, "montecarlopicks.com/feed": the name someone gave it
 * ("nasa") means nothing in a sentence (backlog dabab02c). "your feed" when
 * there's no address to show.
 */
export function feedAddress(url: string | null | undefined): string {
  const bare = String(url || '')
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '');
  return bare || 'your feed';
}

/** The single "Bring into TinyCMS" confirm's set-apart note (Prototype). */
export function bringOneNote(url: string | null | undefined): string {
  return `Later changes to it in ${feedAddress(url)} won’t come through. tinycms keeps it as it is right now.`;
}

export type Banner =
  | {
      kind: 'offer';
      title: string;
      body: string;
      /** The feed "Show them" filters to; null: every feed's waiting articles. */
      feedId: string | null;
    }
  | { kind: 'running'; title: string; count: string; percent: number; body: string }
  | {
      kind: 'done';
      title: string;
      body: string;
      flagged: number;
      showFlagged: string | null;
      checks: BannerCheck[];
    }
  | {
      kind: 'partial';
      body: string;
      failed: number;
      showFailed: string;
      flagged: number;
      showFlagged: string | null;
      checks: BannerCheck[];
    };

/**
 * What checks found, by check (backlog 2e9e8d9b): "No author · 2,540" each
 * with its own filter, not one count of nearly everything. These are things
 * to fix; nothing waits on approval because of them (To approve is that).
 */
function bannerChecks(job: BringInJob): BannerCheck[] {
  return (job.checks || [])
    .slice(0, CHECKS_SHOWN)
    .map((c) => ({ code: c.code, label: c.label, count: num(c.count) }));
}

/**
 * What the banner says, or null for no banner: a running job, else a finished
 * one not yet dismissed, else an offer while feed articles are waiting. The
 * offer goes away when no feed articles are left.
 */
export function bannerFor(status: BringInStatus | null | undefined): Banner | null {
  if (!status?.available) return null;
  const job = status.job;
  const feeds = waitingFeeds(status);
  if (job && job.status === 'running') {
    const from = job.dataSourceId
      ? status.feeds.find((f) => f.id === job.dataSourceId)?.name || 'your feed'
      : feedLabel(feeds.length ? feeds : status.feeds);
    return {
      kind: 'running',
      title: `Bringing in articles from ${from}`,
      count: `${num(job.done)} of ${num(job.total)}`,
      percent: job.total ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0,
      // Bylines and tags from the feed start showing, so not "nothing changes".
      body: 'Nothing is taken down. You can keep working, and we’ll email you when it’s done.',
    };
  }
  if (job && job.status === 'done') return doneBanner(job);
  if (!feeds.length) return null;
  const waiting = feeds.reduce((n, f) => n + f.waiting, 0);
  const live = feeds.reduce((n, f) => n + f.live, 0);
  const shown =
    live === waiting
      ? waiting === 1
        ? 'It’s live as your feed sent it.'
        : 'They’re live as your feed sent them.'
      : live === 0
        ? waiting === 1
          ? 'tinycms shows it as your feed sent it.'
          : 'tinycms shows them as your feed sent them.'
        : `${num(live)} of them are live as your feed sent them.`;
  return {
    kind: 'offer',
    title: `${articles(waiting)} came from ${feedLabel(feeds)}`,
    body: `${shown} Bring ${waiting === 1 ? 'it' : 'them'} in to edit, check and approve ${
      waiting === 1 ? 'it' : 'them'
    } here.`,
    feedId: feeds.length === 1 ? feeds[0].id : null,
  };
}

function doneBanner(job: BringInJob): Banner {
  const flagged = job.flagged;
  const checks = bannerChecks(job);
  const byCheck = checks.length > 0;
  // By check, "Show all" is the rest; otherwise the one count, as before.
  const showFlagged = flagged ? (byCheck ? 'Show all' : `Show the ${num(flagged)}`) : null;
  const inNow = `${articles(job.brought)} ${job.brought === 1 ? 'is' : 'are'} in tinycms`;
  if (job.failed) {
    const r = job.failedReasons;
    const one = job.failed === 1;
    const why =
      r.gone === job.failed
        ? ` because the feed no longer has ${one ? 'it' : 'them'}`
        : r.noTitle === job.failed
          ? ` because ${one ? 'it has' : 'they have'} no title`
          : '';
    const still =
      job.failedLive === job.failed
        ? one
          ? 'It’s still live as it was.'
          : 'They’re still live as they were.'
        : job.failedLive === 0
          ? one
            ? 'It’s as it was.'
            : 'They’re as they were.'
          : 'They’re as they were: the live ones are still live.';
    // "Show the 312" beside "Show the 16" needs its sentence too.
    const found = flagged
      ? byCheck
        ? ` Checks found things to fix in ${num(flagged)} of those brought in. Nothing was taken down.`
        : ` Checks found something to look at in ${num(flagged)} of those brought in.`
      : '';
    return {
      kind: 'partial',
      body: `${inNow}. ${num(job.failed)} couldn’t be brought in${why}. ${still}${found}`,
      failed: job.failed,
      showFailed: `Show the ${num(job.failed)}`,
      flagged,
      showFlagged,
      checks,
    };
  }
  const found = flagged
    ? byCheck
      ? `Checks found things to fix in ${num(flagged)} of them. Nothing was taken down, and fixing them is up to you.`
      : `Checks found something to look at in ${num(flagged)} of them.`
    : 'Checks found nothing to look at.';
  const live =
    job.drafts === 0 && job.live > 0
      ? job.live === 1
        ? 'It’s still live.'
        : 'They’re all still live.'
      : job.live === 0
        ? job.drafts === 1
          ? 'It came in as a draft.'
          : 'They came in as drafts.'
        : 'The live ones are still live.';
  return { kind: 'done', title: inNow, body: `${found} ${live}`, flagged, showFlagged, checks };
}

export interface ConfirmWords {
  title: string;
  stays: string;
  checks: string;
  /** What can't be undone, set apart in a note box (Prototype, backlog 057fcb48). */
  note: string;
  option: string;
  optionNote: string;
  estimate: string;
}

/** The "Bring all in" confirm (design E), for the feeds still waiting. */
export function confirmWords(status: BringInStatus): ConfirmWords {
  const feeds = waitingFeeds(status);
  const waiting = feeds.reduce((n, f) => n + f.waiting, 0);
  const live = feeds.reduce((n, f) => n + f.live, 0);
  const readersSeeRow = status.liveSource === 'row';
  // They come in with the author and tags the feed gave them (the server's
  // feedClassification.js), which the site then shows: nothing ELSE changes.
  const keeps =
    'Each one keeps its words and pictures, and shows the author and tags your feed gave it.';
  const stays = !readersSeeRow
    ? // Only approved versions show on this site, so readers don't see these yet.
      'Readers don’t see them yet: this site shows only approved articles. They come in as drafts, and go live when you approve them.'
    : live === 0
      ? `${keeps} Drafts stay drafts, so nothing on your site changes.`
      : live === waiting
        ? `${keeps} Live articles stay live as version 1, recorded as approved by you. Nothing else on your site changes.`
        : `${keeps} Live articles stay live as version 1, recorded as approved by you. Drafts stay drafts. Nothing else on your site changes.`;
  return {
    title: `Bring ${articles(waiting)} into tinycms?`,
    stays,
    checks:
      'Checks run on all of them. Anything they find is listed for you, and nothing is taken down.',
    note: 'Later changes to these articles in your feed won’t come through. tinycms keeps each one as it is right now.',
    option: `Bring in new articles from ${feedLabel(feeds)} as they arrive`,
    optionNote: 'They arrive as drafts and go live when you approve them.',
    estimate: `${estimate(waiting)} You can keep working.`,
  };
}
